import type {
  ApprovalAction,
  ModifierGroupRule,
  OrderType,
  RemoteStatusResult,
  RemoteStep,
} from '@didto/domain';
import {
  MAX_LINE_QTY,
  REMOTE_POLL_MS,
  formatMinor,
  remoteStep,
  secondsUntil,
  validateLineSelection,
} from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  Loading,
  Notice,
  PinPad,
  Select,
  Sheet,
  Textarea,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  approveRequest,
  askRemoteApproval,
  remoteApprovalStatus,
  requestApproval,
  withdrawRemoteApproval,
} from '@/lib/api';
import { errorText, toAppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { LEDGER_ROW_TONE } from '@/lib/status';
import { uuidv7 } from '@didto/domain';
import { approvalSummaryLines } from './approvals';
import type { PosProduct, PosTable, UiLine } from './posData';
import { describeLine } from './posData';
import './pos.css';

/* ── Approval grants (ADR-023; who approves what: ADR-022) ─────────────────────────────── */

/** A single-use grant for one exact operation: the command carries it instead of a PIN. */
export interface Approval {
  approval_grant_id: string;
}
export interface ApprovalRequest {
  /** Client id of the approval request (a retry after a dropped connection reuses it). */
  requestId: string;
  /** The command that needs the approval and the payload it will send. */
  kind: ApprovalAction;
  payload: Record<string, unknown>;
  title: string;
  reason: string;
  /** Re-sends the command with the grant, or without one when none is needed any more. */
  onApproved: (approval?: Approval) => Promise<void> | void;
}

const APPROVAL_ERRORS: Record<string, string> = {
  pin_invalid: 'Wrong PIN',
  pin_locked: 'PIN locked after too many attempts',
  pin_not_set: 'This approver has no PIN yet',
  pin_six_digits_required:
    'This approval needs the approver’s 6-digit PIN. Set a 6-digit PIN under Employees.',
  employee_suspended: 'This approver is suspended',
  approver_not_permitted: 'This employee cannot approve that',
  self_approval: 'You cannot approve your own action',
  request_not_found: 'This approval request was not found; close and try again',
  request_expired: 'This approval request expired; close and try again',
  request_declined: 'The owner declined this on their phone; close and try again',
  request_withdrawn: 'This request was taken back from the owner’s phone; close and try again',
  session_required: 'Your terminal session has ended',
  terminal_revoked: 'This terminal was revoked',
};

/** Any refusal without a sentence of its own: words for staff, never the code (ADR-037). */
const APPROVAL_FAILED =
  'The approval did not go through. Close and try again; if it keeps happening, tell the owner.';
const APPROVAL_UNAVAILABLE =
  'This cannot be approved here. Close and try again; if it keeps happening, tell the owner.';
const REMOTE_NOT_SENT =
  'This could not be sent to the owner’s phone. Try again; if it keeps happening, tell the owner.';

/** The refusal as the approver reads it, with the attempts left or the wait when the server says. */
function approvalErrorText(v: {
  code: string;
  attempts_left?: number;
  retry_after_seconds?: number;
}): string {
  const base = APPROVAL_ERRORS[v.code] ?? APPROVAL_FAILED;
  if (v.code === 'pin_invalid' && typeof v.attempts_left === 'number')
    return `${base}. ${v.attempts_left} ${v.attempts_left === 1 ? 'attempt' : 'attempts'} left.`;
  if (v.code === 'pin_locked' && typeof v.retry_after_seconds === 'number')
    return `${base}; try again in ${Math.max(1, Math.ceil(v.retry_after_seconds / 60))} min.`;
  if (v.code === 'pin_locked') return `${base}; try again later.`;
  return base;
}

/**
 * The tablet is handed to someone who can approve (at didto. the cashier or the owner, ADR-022).
 * The server first records exactly what is being approved and lists who may approve it; the
 * approver checks the details, picks their name and enters their PIN. A correct PIN returns a
 * short-lived, single-use grant for that operation only, and the command is re-sent with it.
 */
export function ApprovalSheet({
  request,
  onClose,
}: {
  request: ApprovalRequest | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={!!request} onClose={onClose} title={request?.title ?? 'Approval'}>
      {request && <ApprovalBody key={request.requestId} request={request} onClose={onClose} />}
    </Sheet>
  );
}

function ApprovalBody({ request, onClose }: { request: ApprovalRequest; onClose: () => void }) {
  const asked = useQuery({
    queryKey: ['approval-request', request.requestId],
    queryFn: () => requestApproval(request.kind, request.payload, request.requestId),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const online = useOnline();
  const [approverId, setApproverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // ADR-033: the request sent to the owner's phone, and how it ended when it did not end in a grant
  const [remote, setRemote] = useState<'idle' | 'asking' | 'waiting'>('idle');
  const [remoteStop, setRemoteStop] = useState<Extract<RemoteStep, { step: 'stop' }> | null>(null);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const waitingOn = useRef<string | null>(null);
  // taken back by the cashier: an approval that arrives anyway (the owner was quicker, or a read was already on its
  // way) is never sent on its own; the cashier decides
  const withdrawn = useRef(false);
  const [lateGrant, setLateGrant] = useState<string | null>(null);
  // closed (or replaced by another approval) while a call was in flight: its answer is dropped, so
  // nothing is sent and a newer sheet is never closed by an older one
  const open = useRef(true);
  useEffect(() => {
    open.current = true;
    return () => {
      open.current = false;
      // closed while the owner's phone still had it: take it back, so the owner is not asked for nothing
      const waiting = waitingOn.current;
      waitingOn.current = null;
      if (waiting) void withdrawRemoteApproval(waiting).catch(() => undefined);
    };
  }, []);

  // the sheet closes before the command is re-sent, so a refusal can open a fresh approval
  const proceed = async (approval?: Approval) => {
    if (!open.current) return;
    waitingOn.current = null;
    onClose();
    await request.onApproved(approval);
  };

  /** What the tablet read about the request on the owner's phone: wait, send with the grant, or say why not. */
  const onRemote = (r: RemoteStatusResult) => {
    if (!open.current) return;
    if (!r.ok && !r.state) {
      waitingOn.current = null;
      setRemote('idle');
      setRemoteError(APPROVAL_ERRORS[r.code] ?? REMOTE_ERRORS[r.code] ?? REMOTE_NOT_SENT);
      return;
    }
    const next = remoteStep({ ...r, state: r.state ?? 'waiting' });
    if (withdrawn.current) {
      if (next.step === 'proceed') setLateGrant(next.grantId);
      else if (next.step === 'stop') setRemoteStop(next);
      setRemote('idle');
      return;
    }
    if (next.step === 'proceed') {
      void proceed({ approval_grant_id: next.grantId });
    } else if (next.step === 'stop') {
      waitingOn.current = null;
      setRemote('idle');
      setRemoteStop(next);
    } else {
      setRemote('waiting');
    }
  };

  const askRemote = async () => {
    const view = asked.data;
    if (!view?.ok) return;
    setRemote('asking');
    setRemoteError(null);
    setRemoteStop(null);
    try {
      const r = await askRemoteApproval(view.request_id);
      if (r.ok && r.state === 'waiting') waitingOn.current = view.request_id;
      onRemote(r);
    } catch (e) {
      if (!open.current) return;
      setRemote('idle');
      setRemoteError(toAppError(e).message);
    }
  };

  // while the phone has it, read where it stands; the read never keeps the PIN session awake
  const requestId = asked.data?.ok ? asked.data.request_id : null;
  useQuery({
    queryKey: ['approval-remote', requestId],
    enabled: remote === 'waiting' && !!requestId,
    queryFn: async () => {
      const r = await remoteApprovalStatus(requestId!);
      onRemote(r);
      return r;
    },
    refetchInterval: REMOTE_POLL_MS.tablet,
    gcTime: 0,
    retry: false,
  });

  const submit = async (pin: string) => {
    const view = asked.data;
    if (!view?.ok || !approverId) return;
    setBusy(true);
    setError(null);
    try {
      const v = await approveRequest(view.request_id, approverId, pin);
      if (!open.current) return;
      if (!v.ok) {
        setError(approvalErrorText(v));
        setAttempt((n) => n + 1);
        return;
      }
      await proceed({ approval_grant_id: v.grant_id });
    } catch (e) {
      if (!open.current) return;
      setError(toAppError(e).message);
      setAttempt((n) => n + 1);
    } finally {
      if (open.current) setBusy(false);
    }
  };

  if (asked.isLoading) return <Loading label="Preparing the approval…" />;
  if (asked.error)
    return (
      <ErrorState
        title="Could not prepare the approval"
        message={errorText(asked.error)}
        onRetry={() => void asked.refetch()}
      />
    );
  const view = asked.data;
  if (!view) return null;
  if (!view.ok) {
    if (view.code === 'not_needed')
      return (
        <div className="d-stack" data-testid="approval-sheet">
          <p className="d-muted">No approval is needed for this any more.</p>
          <div className="d-sheet__actions">
            <Button
              variant="primary"
              disabled={!online}
              onClick={() => void proceed()}
              data-testid="approval-continue"
            >
              Continue
            </Button>
          </div>
        </div>
      );
    return (
      <Notice tone="danger" role="alert">
        {APPROVAL_ERRORS[view.code] ?? APPROVAL_UNAVAILABLE}
      </Notice>
    );
  }
  const approver = view.approvers.find((e) => e.id === approverId);
  return (
    <div className="d-stack" data-testid="approval-sheet">
      <p className="d-muted">{request.reason}</p>
      <ul className="pos-summary" data-testid="approval-summary">
        {approvalSummaryLines(view.summary).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {!approver ? (
        <div className="d-list" data-testid="approver-picker">
          {view.approvers.length === 0 ? (
            <EmptyState title="No one else here who can approve this has a PIN yet">
              Set one under Employees.
            </EmptyState>
          ) : (
            <span className="d-label">Who approves</span>
          )}
          {view.approvers.map((e) => (
            <button
              key={e.id}
              type="button"
              className="d-row d-row--button"
              onClick={() => setApproverId(e.id)}
              data-testid={`approver-${e.id}`}
            >
              <div className="d-row__main">
                <div className="d-row__title">{e.display_name}</div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="d-spread">
            <span className="d-break">
              Approver: <strong>{approver.display_name}</strong>
            </span>
            <Button variant="ghost" size="sm" onClick={() => setApproverId(null)}>
              Change
            </Button>
          </div>
          {/* a shorter entry still goes to the server: it is refused there without being checked
              or counted, with the reason, so an approver on an old short PIN learns to reset it */}
          <PinPad
            key={attempt}
            title={view.six_digit_pin ? 'Approver PIN (6 digits)' : 'Approver PIN'}
            onSubmit={(pin) => void submit(pin)}
            disabled={busy || !online}
            error={error}
          />
        </>
      )}
      {view.remote?.available && (
        <RemotePanel
          state={remote}
          stop={remoteStop}
          error={remoteError}
          expiresAt={view.expires_at}
          lateGrant={lateGrant}
          onAsk={() => void askRemote()}
          onWithdraw={() => {
            const id = waitingOn.current;
            waitingOn.current = null;
            withdrawn.current = true;
            setRemote('idle');
            if (id) void withdrawRemoteApproval(id).then(onRemote, () => undefined);
          }}
          onSendLate={() => {
            if (lateGrant) void proceed({ approval_grant_id: lateGrant });
          }}
          onLeave={onClose}
        />
      )}
    </div>
  );
}

const REMOTE_ERRORS: Record<string, string> = {
  in_person_only:
    'Cash handed to the owner is confirmed at the drawer with the owner’s PIN, not from the phone.',
  no_remote_approver:
    'The owner cannot approve from the phone yet: they set up an authenticator under Settings, Security first.',
  request_granted: 'Already approved.',
  request_used: 'This approval was already used; close and try again.',
  request_grant_expired: 'The approval expired before it was used; close and try again.',
};

const STOP_TEXT: Record<Extract<RemoteStep, { step: 'stop' }>['reason'], string> = {
  declined: 'The owner declined this on their phone.',
  withdrawn: 'The request was taken back from the owner’s phone.',
  expired: 'The request expired before the owner answered. Close and ask again.',
  used: 'That approval was already used. Close and ask again.',
};

/** ADR-033: the owner is not here, so the request goes to the owner's phone and the tablet waits for the answer. */
function RemotePanel({
  state,
  stop,
  error,
  expiresAt,
  lateGrant,
  onAsk,
  onWithdraw,
  onSendLate,
  onLeave,
}: {
  state: 'idle' | 'asking' | 'waiting';
  stop: Extract<RemoteStep, { step: 'stop' }> | null;
  error: string | null;
  expiresAt: string;
  /** Approved on the phone before the cashier took the request back: sent only if the cashier says so. */
  lateGrant: string | null;
  onAsk: () => void;
  onWithdraw: () => void;
  onSendLate: () => void;
  onLeave: () => void;
}) {
  const online = useOnline();
  const [, tick] = useState(0);
  useEffect(() => {
    if (state !== 'waiting') return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [state]);
  if (lateGrant)
    return (
      <div className="pos-remote">
        <Notice
          tone="warning"
          title="The owner had already approved this on their phone."
          testId="approval-remote-late"
          action={
            <span className="d-actions">
              <Button
                variant="primary"
                size="sm"
                disabled={!online}
                onClick={onSendLate}
                data-testid="approval-remote-send-late"
              >
                Send it now
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onLeave}
                data-testid="approval-remote-leave"
              >
                Leave it
              </Button>
            </span>
          }
        >
          <span className="pos-notice-text">Nothing was sent.</span>
        </Notice>
      </div>
    );
  if (stop)
    return (
      <div className="pos-remote">
        <Notice tone="danger" role="alert" testId={`approval-remote-${stop.reason}`}>
          {STOP_TEXT[stop.reason]}
          {stop.note ? ` “${stop.note}”` : ''}
        </Notice>
      </div>
    );
  if (state === 'waiting') {
    const left = secondsUntil(expiresAt);
    return (
      <div className="pos-remote">
        <Notice
          tone="info"
          title="Waiting for the owner’s approval on their phone…"
          testId="approval-remote-waiting"
          action={
            <Button
              variant="ghost"
              size="sm"
              disabled={!online}
              onClick={onWithdraw}
              data-testid="approval-remote-withdraw"
            >
              Take the request back
            </Button>
          }
        >
          <span className="pos-notice-text">
            The owner sees what is asked and approves or declines it there. This continues on its
            own once approved. {left > 0 ? `${Math.ceil(left / 60)} min left.` : ''}
          </span>
        </Notice>
      </div>
    );
  }
  return (
    <div className="pos-remote">
      <div className="d-spread">
        <span className="d-muted">Owner not here?</span>
        <Button
          onClick={onAsk}
          busy={state === 'asking'}
          disabled={!online || state === 'asking'}
          data-testid="approval-remote-ask"
        >
          Ask on the owner’s phone
        </Button>
      </div>
      {error && (
        <div className="d-error" role="alert" data-testid="approval-remote-error">
          {error}
        </div>
      )}
    </div>
  );
}

/* ── Reason prompt (cancel, void) ───────────────────────────────────────────────────────── */

export interface ReasonRequest {
  title: string;
  label: string;
  required: boolean;
  confirmLabel: string;
  danger?: boolean;
  /** What confirming will do, in words and with the amount, shown above the reason. */
  detail?: string;
  onConfirm: (reason: string) => Promise<void> | void;
}

export function ReasonSheet({
  request,
  onClose,
}: {
  request: ReasonRequest | null;
  onClose: () => void;
}) {
  const online = useOnline();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => {
    setText('');
    setBusy(false);
    onClose();
  };
  const confirm = async () => {
    if (!request) return;
    const reason = text.trim();
    if (request.required && !reason) return;
    setBusy(true);
    try {
      await request.onConfirm(reason);
      close();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={!!request} onClose={close} title={request?.title ?? ''}>
      <div className="d-stack">
        {request?.detail && <p className="pos-consequence">{request.detail}</p>}
        <Field
          label={request?.label ?? 'Reason'}
          help={request?.required ? 'Required; it is kept in the audit log.' : 'Optional.'}
        >
          {(id) => (
            <Textarea
              id={id}
              rows={3}
              maxLength={200}
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="reason-text"
            />
          )}
        </Field>
        <div className="d-sheet__actions">
          <Button variant="ghost" onClick={close}>
            Back
          </Button>
          <Button
            variant={request?.danger ? 'danger-solid' : 'primary'}
            busy={busy}
            disabled={!online || (!!request?.required && !text.trim())}
            onClick={() => void confirm()}
            data-testid="reason-confirm"
          >
            {request?.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

/* ── Line picker: variant, modifiers, quantity, notes ───────────────────────────────────── */

export function LineSheet({
  product,
  currency,
  needsServer = false,
  onAdd,
  onClose,
}: {
  product: PosProduct | null;
  currency: string;
  /** The line goes to the server now (an order is open), not to the local cart: paused offline. */
  needsServer?: boolean;
  onAdd: (line: UiLine) => void;
  onClose: () => void;
}) {
  // Remount per product (key on the caller) so the state below starts fresh for each pick.
  return product ? (
    <LinePicker
      key={product.product.id}
      product={product}
      currency={currency}
      needsServer={needsServer}
      onAdd={onAdd}
      onClose={onClose}
    />
  ) : null;
}

function LinePicker({
  product,
  currency,
  needsServer,
  onAdd,
  onClose,
}: {
  product: PosProduct;
  currency: string;
  needsServer: boolean;
  onAdd: (line: UiLine) => void;
  onClose: () => void;
}) {
  const online = useOnline();
  const orderable = product.variants.filter((v) => !product.soldOutVariantIds.has(v.id));
  const initial = orderable.find((v) => v.is_default) ?? orderable[0] ?? null;
  const [variantId, setVariantId] = useState<string | null>(initial?.id ?? null);
  const [selected, setSelected] = useState<string[]>([]);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');

  const variant = product.variants.find((v) => v.id === variantId) ?? null;
  const showVariants = product.variants.length > 1;
  const optionById = new Map(
    product.groups.flatMap((g) => g.options.map((o) => [o.id, { o, g }] as const)),
  );
  const chosen = selected.map((id) => optionById.get(id)).filter((x) => x !== undefined);
  const selectionError = validateLineSelection(
    product.groups.map((g) => g.rule),
    selected.map((id) => ({ modifier_option_id: id })),
  );
  const unit = (variant?.price_minor ?? 0) + chosen.reduce((a, x) => a + x.o.price_delta_minor, 0);

  const toggle = (groupId: string, optionId: string, single: boolean) => {
    setSelected((cur) => {
      if (cur.includes(optionId)) return cur.filter((x) => x !== optionId);
      const others = single ? cur.filter((id) => optionById.get(id)?.g.group.id !== groupId) : cur;
      return [...others, optionId];
    });
  };

  const add = () => {
    if (!variant || selectionError) return;
    onAdd({
      id: uuidv7(),
      product_id: product.product.id,
      product_variant_id: variant.id,
      name: product.product.name,
      variant_name: showVariants ? (variant.name ?? 'Default') : variant.name,
      unit_price_minor: variant.price_minor,
      qty,
      notes,
      modifiers: chosen.map((x) => ({
        modifier_option_id: x.o.id,
        group_name: x.g.group.name,
        name: x.o.name,
        price_delta_minor: x.o.price_delta_minor,
      })),
    });
  };

  return (
    <Sheet open onClose={onClose} title={product.product.name}>
      <div className="pos-pick" data-testid="line-sheet">
        {showVariants && (
          <div className="pos-group">
            <div className="pos-group__head">
              <span className="pos-group__name">Size / variant</span>
              <span className="pos-group__rule">Choose 1</span>
            </div>
            <div className="pos-opts" role="group" aria-label="Variant">
              {product.variants.map((v) => {
                const out = product.soldOutVariantIds.has(v.id);
                return (
                  <button
                    key={v.id}
                    type="button"
                    className="pos-opt pos-opt--single"
                    aria-pressed={v.id === variantId}
                    disabled={out}
                    onClick={() => setVariantId(v.id)}
                    data-testid={`variant-${v.id}`}
                  >
                    <span className="pos-opt__name">{v.name ?? 'Default'}</span>
                    <span className="pos-opt__price">
                      {formatMinor(v.price_minor, currency)}
                      {out ? ' · sold out' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {product.groups.map((g) => {
          const single = g.rule.max_select === 1;
          const count = selected.filter(
            (id) => optionById.get(id)?.g.group.id === g.group.id,
          ).length;
          const full = g.rule.max_select !== null && count >= g.rule.max_select && !single;
          const ruleId = `rule-${g.group.id}`;
          return (
            <div className="pos-group" key={g.group.id}>
              <div className="pos-group__head">
                <span className="pos-group__name">{g.group.name}</span>
                <span
                  id={ruleId}
                  className={`pos-group__rule${count < g.rule.min_select ? ' pos-group__rule--needed' : ''}`}
                >
                  {ruleText(g.rule)}
                </span>
              </div>
              <div
                className="pos-opts"
                role="group"
                aria-label={g.group.name}
                aria-describedby={ruleId}
              >
                {g.options.length === 0 && <span className="d-muted">No options configured.</span>}
                {g.options.map((o) => {
                  const on = selected.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className={`pos-opt${single ? ' pos-opt--single' : ''}`}
                      aria-pressed={on}
                      disabled={!on && full}
                      onClick={() => toggle(g.group.id, o.id, single)}
                      data-testid={`option-${o.id}`}
                    >
                      <span className="pos-opt__name">{o.name}</span>
                      {o.price_delta_minor !== 0 && (
                        <span className="pos-opt__price">
                          {o.price_delta_minor > 0 ? '+' : '−'}
                          {formatMinor(Math.abs(o.price_delta_minor), currency)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div className="pos-qty-row">
          <span className="d-label">Quantity</span>
          <QtyStepper value={qty} onChange={setQty} testId="line-qty" />
        </div>
        <Field label="Notes for the kitchen">
          {(id) => (
            <Input
              id={id}
              value={notes}
              maxLength={200}
              placeholder="e.g. less ice"
              onChange={(e) => setNotes(e.target.value)}
              data-testid="line-notes"
            />
          )}
        </Field>
        <div className="pos-sheet-foot pos-sheet-foot--sticky">
          <div className="pos-sheet-foot__sum">
            <strong className="pos-sheet-foot__amount" data-testid="line-total">
              {formatMinor(unit * qty, currency)}
            </strong>
            {qty > 1 && (
              <span className="d-muted d-num">
                {qty} × {formatMinor(unit, currency)}
              </span>
            )}
            {selectionError && (
              <span className="pos-problem" role="status" data-testid="line-rule">
                {selectionError}
              </span>
            )}
          </div>
          <div className="pos-sheet-foot__actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={(needsServer && !online) || !variant || !!selectionError}
              onClick={add}
              data-testid="line-add"
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/** A group's rule in words: whether it is required and how many may be chosen. */
function ruleText(r: ModifierGroupRule): string {
  const min = r.min_select;
  const max = r.max_select;
  if (min <= 0) return max === null ? 'Optional' : `Optional · up to ${max}`;
  if (max === min) return `Required · choose ${min}`;
  if (max === null) return `Required · at least ${min}`;
  return `Required · choose ${min} to ${max}`;
}

export function QtyStepper({
  value,
  onChange,
  min = 1,
  testId,
  disabled,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  testId?: string;
  disabled?: boolean;
}) {
  return (
    <span className="d-qty" data-testid={testId}>
      <IconButton
        size="sm"
        label="decrease quantity"
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
      >
        −
      </IconButton>
      <span className="d-qty__value" aria-live="polite">
        {value}
      </span>
      <IconButton
        size="sm"
        label="increase quantity"
        disabled={disabled || value >= MAX_LINE_QTY}
        onClick={() => onChange(value + 1)}
      >
        +
      </IconButton>
    </span>
  );
}

/* ── Order header (type, table, guest, schedule) ────────────────────────────────────────── */

export interface HeaderState {
  type: OrderType;
  dining_table_id: string;
  covers: string;
  customer_name: string;
  /** Wall time in the location timezone as typed (`YYYY-MM-DDTHH:mm`), converted on submit. */
  scheduled_wall: string;
  notes: string;
}
export const EMPTY_HEADER: HeaderState = {
  type: 'takeout',
  dining_table_id: '',
  covers: '',
  customer_name: '',
  scheduled_wall: '',
  notes: '',
};

const TYPE_OPTIONS: { value: OrderType; label: string }[] = [
  { value: 'takeout', label: 'Takeout' },
  { value: 'dine_in', label: 'Dine-in' },
  { value: 'advance', label: 'Advance' },
];

export function OrderHeaderFields({
  value,
  onChange,
  tables,
  noTables,
  occupied,
  timeZone,
  compact,
}: {
  value: HeaderState;
  onChange: (next: HeaderState) => void;
  tables: PosTable[];
  /** The location has no tables set up (known, not still loading): no table or guests fields to show. */
  noTables?: boolean;
  occupied: ReadonlySet<string>;
  timeZone: string;
  compact?: boolean;
}) {
  const set = <K extends keyof HeaderState>(k: K, v: HeaderState[K]) =>
    onChange({ ...value, [k]: v });
  return (
    <div className="pos-fields">
      <div
        className="d-pills pos-type"
        role="group"
        aria-label="Order type"
        data-testid="order-type"
      >
        {TYPE_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className="d-pill"
            aria-pressed={o.value === value.type}
            onClick={() => set('type', o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      {value.type === 'dine_in' && noTables && (
        <p className="d-help" data-testid="order-no-tables">
          No tables are set up yet. The owner adds them under Tables.
        </p>
      )}
      {value.type === 'dine_in' && !noTables && (
        <div className="pos-fields__pair">
          <Field label="Table">
            {(id) => (
              <Select
                id={id}
                value={value.dining_table_id}
                onChange={(e) => set('dining_table_id', e.target.value)}
                data-testid="order-table"
              >
                <option value="">Choose a table…</option>
                {tables.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {occupied.has(t.id) ? ' · occupied' : ''}
                    {t.seats ? ` · ${t.seats} seats` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Guests">
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={1}
                max={99}
                value={value.covers}
                onChange={(e) => set('covers', e.target.value)}
                data-testid="order-covers"
              />
            )}
          </Field>
        </div>
      )}
      {value.type === 'advance' && (
        <Field label="Ready at" help={`Location time (${timeZone}).`}>
          {(id) => (
            <Input
              id={id}
              type="datetime-local"
              value={value.scheduled_wall}
              onChange={(e) => set('scheduled_wall', e.target.value)}
              data-testid="order-scheduled"
            />
          )}
        </Field>
      )}
      <Field label={value.type === 'takeout' ? 'Name for the order' : 'Customer name'}>
        {(id) => (
          <Input
            id={id}
            value={value.customer_name}
            maxLength={80}
            placeholder="optional"
            onChange={(e) => set('customer_name', e.target.value)}
            data-testid="order-customer"
          />
        )}
      </Field>
      {!compact && (
        <Field label="Order notes">
          {(id) => (
            <Input
              id={id}
              value={value.notes}
              maxLength={200}
              placeholder="optional"
              onChange={(e) => set('notes', e.target.value)}
              data-testid="order-notes"
            />
          )}
        </Field>
      )}
    </div>
  );
}

/* ── Small display helpers shared with the Orders page ──────────────────────────────────── */

export function LineBadge({ status, sentAt }: { status: string; sentAt: string | null }) {
  if (status === 'voided') return <Badge tone={LEDGER_ROW_TONE.voided}>voided</Badge>;
  return sentAt ? (
    <Badge tone={LEDGER_ROW_TONE.sent}>sent</Badge>
  ) : (
    <Badge tone={LEDGER_ROW_TONE.not_sent}>not sent</Badge>
  );
}

export { describeLine };
